/* ──────────────────────────────────────────────────────────────────────
 *  ImpactPreviewModal — Read-only preview for Impact page items
 *
 *  Supports three content types:
 *    - chronicle  → fetches full ChronicleResponse, shows narrative + timeline + metrics
 *    - knowledge  → fetches full BlogArticleDetail, shows body preview
 *    - role       → receives UserRoleAssignmentResponse inline (no fetch), shows full detail
 *
 *  Opens as a portal overlay so the user stays on My Impact while browsing.
 *  Footer provides "View Full" (navigates to full page) and "Edit" (if owner).
 * ────────────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CloseButton from '../CloseButton/CloseButton';
import { fetchChronicle } from '../../api/chronicles';
import { fetchArticle } from '../../api/blogArticles';
import type { ChronicleResponse } from '../../api/chronicles';
import type { BlogArticleDetail } from '../../api/blogArticles';
import type { UserRoleAssignmentResponse } from '../../api/impactSignature';
import type { BlogDraft } from '../../Data/Blog/blogDraftStore';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './ImpactPreviewModal.module.css';

// ── localStorage fallback for knowledge articles ──────────────

const STORAGE_KEY = 'l4e_blog_drafts';

function loadDraftById(draftId: string): BlogDraft | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw) as BlogDraft[];
    return all.find(d => d.draftId === draftId) ?? null;
  } catch {
    return null;
  }
}

/** Convert a localStorage BlogDraft to BlogArticleDetail shape for the preview. */
function draftToArticleDetail(d: BlogDraft): BlogArticleDetail {
  return {
    articleGuid: d.draftId,
    slug: '',
    blogType: d.blogType,
    status: d.status,
    title: d.headline || 'Untitled',
    subtitle: d.subtitle || undefined,
    excerpt: d.bodyText?.slice(0, 140) || undefined,
    category: d.category || undefined,
    tags: d.tags ?? [],
    featuredImageUrl: d.heroImageUrl || undefined,
    authorGuid: '',
    authorDisplayName: d.byline || undefined,
    publishedAt: d.updatedAt,
    createdAt: d.createdAt,
    body: d.bodyText ?? '',
    bodyFormat: 'plaintext',
    language: 'en',
    version: 1,
    updatedAt: d.updatedAt,
    citations: [],
  };
}

// ── Dimension colour map (mirrors ChroniclesPanel) ──────────────

/** Map raw categoryKey to localized label key (pages namespace). */
const CHRONICLE_CATEGORY_KEYS: Record<string, string> = {
  success_story: 'chronicleEditor.categorySuccessStory',
  case_study: 'chronicleEditor.categoryCaseStudy',
  reflection: 'chronicleEditor.categoryReflection',
  initiative: 'chronicleEditor.categoryInitiative',
};

/** Map raw blogType to localized label key (userProfile namespace). */
const BLOG_TYPE_KEYS: Record<string, string> = {
  professional_news: 'Professional News',
  opinion: 'Opinion',
  personal: 'Personal',
  scholarly: 'Scholarly',
  how_to: 'How-To',
  best_practice: 'Best Practice',
  lesson_learned: 'Lesson Learned',
};

const DIMENSION_COLORS: Record<string, string> = {
  mind:  'var(--dim-mind, #7ea3cc)',
  body:  'var(--dim-body, #6aab7a)',
  heart: 'var(--dim-heart, #c77a6f)',
  wings: 'var(--dim-wings, #c9a24e)',
  roots: 'var(--dim-roots, #8B7355)',
  voice: 'var(--dim-voice, #d17eb2)',
};

// ── Types ────────────────────────────────────────────────────────

export type ImpactPreviewTarget =
  | { type: 'chronicle'; guid: string }
  | { type: 'knowledge'; guid: string }
  | { type: 'role'; data: UserRoleAssignmentResponse; heroUrl?: string | null };

interface Props {
  target: ImpactPreviewTarget;
  onClose: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────

function resolveImageUrl(raw: string | undefined | null): string | null {
  return mediaSrc(raw);
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ── Sub-views ────────────────────────────────────────────────────

function ChroniclePreview({ data }: { data: ChronicleResponse }) {
  const { t } = useAppTranslation('pages');
  const heroUrl = resolveImageUrl(data.heroImageUrl);

  return (
    <>
      {heroUrl && <img className={styles.heroImage} src={heroUrl} alt="" />}

      {/* Meta row */}
      <div className={styles.metaRow}>
        <span className={styles.metaDate}>{shortDate(data.createdAt)}</span>
        <span className={styles.metaChip}>
          {CHRONICLE_CATEGORY_KEYS[data.categoryKey]
            ? t(CHRONICLE_CATEGORY_KEYS[data.categoryKey])
            : data.categoryKey}
        </span>
        {data.status === 'draft' && (
          <span className={`${styles.statusBadge} ${styles.statusDraft}`}>
            {t('impact.chroniclesDraft')}
          </span>
        )}
        {data.dimensionTags.length > 0 && (
          <div className={styles.dimensionDots}>
            {data.dimensionTags.map((dim) => (
              <span
                key={dim}
                className={styles.dimensionDot}
                style={{ backgroundColor: DIMENSION_COLORS[dim] ?? 'var(--text-muted)' }}
                title={dim}
              />
            ))}
          </div>
        )}
      </div>

      {/* Mission narrative */}
      {data.missionNarrative && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewMission')}</div>
          <div className={styles.sectionText}>{data.missionNarrative}</div>
        </div>
      )}

      {/* Background */}
      {data.background && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewBackground')}</div>
          <div className={styles.sectionText}>{data.background}</div>
        </div>
      )}

      {/* Outcome metrics */}
      {data.outcomeMetrics.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewMetrics')}</div>
          <div className={styles.metricsGrid}>
            {data.outcomeMetrics.map((m) => (
              <div key={m.metricGuid} className={styles.metricCard}>
                <div className={styles.metricLabel}>{m.label}</div>
                <div className={styles.metricValue}>
                  {m.actualValue ?? '—'}{m.unit ? ` ${m.unit}` : ''}
                </div>
                {m.targetValue != null && (
                  <div className={styles.metricTarget}>
                    {t('impact.previewTarget')}: {m.targetValue}{m.unit ? ` ${m.unit}` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {data.timelineEvents.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewTimeline')}</div>
          <div className={styles.timelineList}>
            {[...data.timelineEvents]
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((ev) => (
                <div key={ev.eventGuid} className={styles.timelineItem}>
                  <span className={styles.timelineDate}>{ev.dateLabel}</span>
                  <span>{ev.eventTitle}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
}

function KnowledgePreview({ data }: { data: BlogArticleDetail }) {
  const { t } = useAppTranslation('pages');
  const heroUrl = resolveImageUrl(data.featuredImageUrl);

  return (
    <>
      {heroUrl && <img className={styles.heroImage} src={heroUrl} alt="" />}

      <div className={styles.metaRow}>
        <span className={styles.metaDate}>{shortDate(data.publishedAt ?? data.createdAt)}</span>
        {data.category && <span className={styles.metaChip}>{data.category}</span>}
        {data.blogType && (
          <span className={styles.metaChip}>{BLOG_TYPE_KEYS[data.blogType] ?? data.blogType}</span>
        )}
        <span className={`${styles.statusBadge} ${styles.statusPublished}`}>
          {t('impact.knowledgePublished')}
        </span>
      </div>

      {data.subtitle && (
        <div className={styles.section}>
          <div className={styles.sectionText} style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>
            {data.subtitle}
          </div>
        </div>
      )}

      {/* Body preview — render first ~1000 chars of markdown as plain text */}
      {data.body && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewContent')}</div>
          <div className={styles.sectionText}>
            {data.body.length > 1000 ? data.body.slice(0, 1000) + '…' : data.body}
          </div>
        </div>
      )}

      {data.tags && data.tags.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewTags')}</div>
          <div className={styles.skillsList}>
            {data.tags.map((tag) => (
              <span key={tag} className={styles.skillChip}>{tag}</span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function RolePreview({ data, heroUrl }: { data: UserRoleAssignmentResponse; heroUrl?: string | null }) {
  const { t } = useAppTranslation('pages');

  const statusClass =
    data.status === 'active' ? styles.statusActive
    : data.status === 'completed' ? styles.statusCompleted
    : '';

  return (
    <>
      {heroUrl && <img className={styles.heroImage} src={heroUrl} alt="" />}

      <div className={styles.metaRow}>
        <span className={styles.metaDate}>
          {shortDate(data.assignedAt)}
          {data.completedAt ? ` — ${shortDate(data.completedAt)}` : ''}
        </span>
        <span className={`${styles.statusBadge} ${statusClass}`}>
          {t(`impact.roleStatus.${data.status}`)}
        </span>
      </div>

      {/* Role description */}
      {data.roleDescription && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewRoleDescription')}</div>
          <div className={styles.sectionText}>{data.roleDescription}</div>
        </div>
      )}

      {/* Time commitment & duration */}
      {(data.timeCommitment || data.durationType) && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewCommitment')}</div>
          <div className={styles.sectionText}>
            {[
              data.timeCommitment,
              data.durationType !== 'ongoing' ? data.durationType : null,
              data.expectedStartDate ? `${t('impact.previewFrom')} ${shortDate(data.expectedStartDate)}` : null,
              data.expectedEndDate ? `${t('impact.previewTo')} ${shortDate(data.expectedEndDate)}` : null,
            ].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}

      {/* Skills required */}
      {data.skillsRequired && data.skillsRequired.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewSkills')}</div>
          <div className={styles.skillsList}>
            {data.skillsRequired.map((skill) => (
              <span key={skill} className={styles.skillChip}>{skill}</span>
            ))}
          </div>
        </div>
      )}

      {/* Notes / instructions */}
      {data.instructions && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t('impact.previewInstructions')}</div>
          <div className={styles.sectionText}>{data.instructions}</div>
        </div>
      )}
    </>
  );
}

// ── Main component ───────────────────────────────────────────────

export default function ImpactPreviewModal({ target, onClose }: Props) {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();

  const [chronicle, setChronicle] = useState<ChronicleResponse | null>(null);
  const [article, setArticle] = useState<BlogArticleDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---- Fetch data on open ---- */
  useEffect(() => {
    let cancelled = false;

    if (target.type === 'chronicle') {
      setLoading(true);
      fetchChronicle(target.guid)
        .then((d) => { if (!cancelled) { setChronicle(d); setLoading(false); } })
        .catch((e) => { if (!cancelled) { setError(e?.message ?? 'Load failed'); setLoading(false); } });
    } else if (target.type === 'knowledge') {
      setLoading(true);
      fetchArticle(target.guid)
        .then((d) => { if (!cancelled) { setArticle(d); setLoading(false); } })
        .catch(() => {
          // API failed — fall back to localStorage draft
          if (cancelled) return;
          const draft = loadDraftById(target.guid);
          if (draft) {
            setArticle(draftToArticleDetail(draft));
            setLoading(false);
          } else {
            setError('Not found');
            setLoading(false);
          }
        });
    }
    // role type: data is inline, no fetch needed

    return () => { cancelled = true; };
  }, [target]);

  /* ---- Escape key to close ---- */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  /* ---- Derive title & subtitle ---- */
  let title = '';
  let subtitle = '';

  if (target.type === 'chronicle' && chronicle) {
    title = chronicle.title;
    subtitle = chronicle.subtitle || '';
  } else if (target.type === 'knowledge' && article) {
    title = article.title;
    subtitle = article.authorDisplayName || '';
  } else if (target.type === 'role') {
    title = target.data.roleLabel || target.data.roleCategory;
    subtitle = target.data.projectName;
  }

  /* ---- Navigation handlers ---- */
  const handleViewFull = () => {
    onClose();
    if (target.type === 'chronicle' && chronicle) {
      navigate(`/chronicles/${chronicle.chronicleGuid}/view`);
    } else if (target.type === 'knowledge' && article) {
      navigate(`/knowledge/${article.articleGuid}`);
    } else if (target.type === 'role') {
      navigate(`/projects/${target.data.projectGuid}/about`);
    }
  };

  /* ---- Render ---- */
  return ReactDOM.createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.modalTitle}>{title || t('impact.previewLoading')}</span>
            {subtitle && <span className={styles.modalSubtitle}>{subtitle}</span>}
          </div>
          <CloseButton onClick={onClose} size="md" />
        </div>

        {/* Body */}
        <div className={styles.body}>
          {loading && <div className={styles.loading}>{t('impact.previewLoading')}</div>}
          {error && <div className={styles.error}>{error}</div>}

          {!loading && !error && target.type === 'chronicle' && chronicle && (
            <ChroniclePreview data={chronicle} />
          )}
          {!loading && !error && target.type === 'knowledge' && article && (
            <KnowledgePreview data={article} />
          )}
          {!loading && !error && target.type === 'role' && (
            <RolePreview data={target.data} heroUrl={target.heroUrl} />
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.footerBtn} onClick={onClose}>
            {t('impact.previewClose')}
          </button>
          <button
            type="button"
            className={`${styles.footerBtn} ${styles.footerBtnPrimary}`}
            onClick={handleViewFull}
          >
            {target.type === 'role'
              ? t('impact.previewViewProject')
              : t('impact.previewViewFull')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
